import React from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export function Accordion(props) {
  const {
    className,
    children,
    id,
    notDraggable,
    singleActiveIndex,
    onTabChange,
    onItemDragged,
    onOpenTabsChanged,
  } = props;

  const [activeIndex, setActiveIndex] = React.useState(props.activeIndex);
  React.useEffect(() => {
    setActiveIndex(props.activeIndex);
  }, [props.activeIndex]);

  const selectTab = React.useCallback(
    (index) => {
      if (onTabChange) {
        onTabChange({ index });
      } else {
        setActiveIndex(getActiveIndex(index));
      }
    },
    [onTabChange],
  );

  const setIsOpen = React.useCallback(
    (index, isOpen) => {
      if (onOpenTabsChanged) {
        onOpenTabsChanged(index, isOpen);
      }
    },
    [onOpenTabsChanged],
  );

  const getActiveIndex = React.useCallback(
    (index) => {
      return children.length > 0 && index === activeIndex ? null : index;
    },
    [activeIndex, children.length],
  );

  const onDragStart = React.useCallback(() => {
    const holderId = `${id}Holder`;
    const holder = document.getElementById(holderId);
    if (holder) {
      holder.style.color = "orange";
      holder.style.transition = "background-color 0.2s ease";
    }
  }, [id]);

  const onDragUpdate = React.useCallback((update) => {
    // const { destination } = update;
    // const opacity = destination
    //   ? destination.index / Object.keys(this.state.tasks).length
    //   : 0;
    // document.body.style.backgroundColor = `rgba(153, 141, 217, ${opacity})`
  }, []);

  const onDragEnd = React.useCallback(
    (result) => {
      const holderId = `${props.id}Holder`;
      const holder = document.getElementById(holderId);
      if (holder) {
        holder.style.color = "inherit";
        holder.style.backgroundColor = "inherit";
      }

      const { destination, source, draggableId } = result;
      if (!destination) {
        return;
      }
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }
      if (props.onItemDragged) {
        props.onItemDragged(source.index, destination.index);
      }
    },
    [props.id, props.onItemDragged],
  );

  // console.log('Accordion.render()', this.props);

  if (notDraggable) {
    const content = React.Children.toArray(children).map((child, index) => {
      const onToggle = () => selectTab(index);
      const onSetIsOpen = (isOpen) => setIsOpen(index, isOpen);
      const isOpen = index === activeIndex;
      const itemClassName = index == 0 ? "" : "margin-sm-top";
      return React.cloneElement(child, {
        isOpen,
        singleActiveIndex,
        itemClassName,
        onToggle,
        onSetIsOpen,
      });
    });
    return <div className={className}>{content}</div>;
  }
  return (
    <div id={`${id}Holder`} className={className}>
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Droppable droppableId={id}>
          {(provided, snapshot) => (
            <DroppablePanel
              ref={provided.innerRef}
              {...provided.droppableProps}
              isDraggingOver={snapshot.isDraggingOver}
            >
              <InnerList selectTab={selectTab} activeIndex={activeIndex}>
                {children}
              </InnerList>
              {provided.placeholder}
            </DroppablePanel>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
Accordion.propTypes = {
  className: PropTypes.string,
  activeIndex: PropTypes.number,
  onTabChange: PropTypes.func,
  onItemDragged: PropTypes.func,
  children: PropTypes.node,
  notDraggable: PropTypes.bool,
  singleActiveIndex: PropTypes.bool,
  onOpenTabsChanged: PropTypes.func,
};
Accordion.defaultProps = {
  className: "",
  onTabChange: null,
};

const DroppablePanel = React.forwardRef((props, ref) => {
  const Component = ({ forwardedRef, isDraggingOver, children, ...props }) => {
    return (
      <div
        ref={forwardedRef}
        // className={`${isDraggingOver ? 'bg-secondary' : 'w3-white'}`}
        style={{ background: `${isDraggingOver ? "#f5f5f5" : "#fff"}` }}
        {...props}
      >
        {children}
      </div>
    );
  };
  return <Component {...props} forwardedRef={ref} />;
});

function InnerList(props) {
  const { selectTab, activeIndex, children } = props;
  return React.Children.toArray(children).map((child, index) => {
    const onToggle = () => selectTab(index);
    const isOpen = index === activeIndex;
    const itemClassName = index == 0 ? "" : "margin-sm-top";
    return (
      <Draggable key={index} draggableId={`dragItem${index}`} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            className="pad-lr pad-top"
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            {React.cloneElement(child, {
              isOpen,
              singleActiveIndex: true,
              itemClassName,
              onToggle,
              isDragging: snapshot.isDragging,
            })}
          </div>
        )}
      </Draggable>
    );
  });
}

export function AccordionTab(props) {
  const {
    isOpen,
    stateOpen,
    singleActiveIndex,
    onToggle,
    onSetIsOpen,
    className,
    header,
    menu,
    isDragging,
    children,
  } = props;
  const [open, setOpen] = React.useState(
    singleActiveIndex ? isOpen : stateOpen,
  );

  const handleToggle = React.useCallback(() => {
    const isOpen = !open;
    setOpen(isOpen);
    if (onSetIsOpen) {
      onSetIsOpen(isOpen);
    }
    if (singleActiveIndex && onToggle) {
      onToggle();
    }
  }, [open, onSetIsOpen, singleActiveIndex, onToggle]);

  const body = isDragging ? null : (
    <div className={`w3-${open ? "show" : "hide"}`}>
      <div className="body w3-border-left w3-border-right w3-border-bottom">
        {children}
      </div>
    </div>
  );
  return (
    <div className={`${className} w3-white`}>
      <div
        className={`header ${isDragging ? "bg-secondary" : "w3-light-grey"} w3-border`}
      >
        <div className="w3-cell-row">
          <div
            className="w3-cell w3-cell-middle clickable"
            onClick={handleToggle}
          >
            <h4 className="no-margin pad">{header}</h4>
          </div>
          <div
            className="w3-cell w3-cell-middle alg-right pad-rht"
            style={{ width: "180px" }}
          >
            {menu}
          </div>
        </div>
      </div>
      {body}
    </div>
  );
}

AccordionTab.propTypes = {
  className: PropTypes.string,
  onToggle: PropTypes.func,
  header: PropTypes.string,
  menu: PropTypes.node,
  isOpen: PropTypes.bool,
  stateOpen: PropTypes.bool,
  isDragging: PropTypes.bool,
  singleActiveIndex: PropTypes.bool,
  onSetIsOpen: PropTypes.func,
  children: PropTypes.node,
};
AccordionTab.defaultProps = {
  className: "",
  header: "Titel",
};
